import { ReactNode, useEffect, useState } from 'react';
import { TariffCodeSuggestion } from '~/lib/api/smartsend/types';
import { GetTariffCodeFromSuggestion } from './GetTariffCodeFromSuggestion';
import { TariffSuggestion } from './TariffSuggestion';
import { EventSections } from '../../lib/events/EventLogger';
import { TariffCodeCache } from '../../lib/cache/TariffCodeCache';
import SelectOrCopy from '../smartsend/item/SelectOrCopy';
import { P2GBox, P2GLink, P2GLinkGroup, P2GFade, P2GProgress } from '@parcel2go/core-ui';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

interface props {
  selectCode: (code: string) => void;
  itemDescription?: string;
  tariffSystem: string;
  showAllOnLoad?: boolean;
  noResultsMessage?: ReactNode;
  showLoader?: boolean;
  limit: number;
  canSelect: boolean;
  title?: string;
}

const TariffSuggestions: React.FC<props> = (props) => {
  const { suggest } = useSmartSendApi();
  const [suggestions, setSuggestions] = useState<TariffCodeSuggestion[]>([]);
  const [showMore, setShowMore] = useState(!!props.showAllOnLoad);
  const [loading, setLoading] = useState(false);
  const { track } = useAnalytics();

  useEffect(() => {
    setLoading(true);
    if (!props.itemDescription) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    if (TariffCodeCache[props.itemDescription]) {
      setSuggestions(TariffCodeCache[props.itemDescription].slice(0, props.limit));
      setLoading(false);
      return;
    }

    try {
      suggest(props.itemDescription).then((r) => {
        if (r.success && r.result?.Suggestions) {
          const suitable = r.result.Suggestions.filter((x) => !!GetTariffCodeFromSuggestion(props.tariffSystem, x));
          track({
            type: 'tariff_code_suggestions_searched',
            payload: {
              ...GenerateBaseAnalyticsPayload([EventSections.TariffCodeCategory]),
              matches: suitable.length,
              search: props.itemDescription
            }
          });

          // Cache
          TariffCodeCache[props.itemDescription] = suitable;
          setSuggestions(suitable.slice(0, props.limit));
        } else {
          setSuggestions([]);
        }
        setLoading(false);
      });
    } catch {
      setLoading(false);
    }
  }, [props.tariffSystem, props.itemDescription]);

  const onClick = (s: TariffCodeSuggestion) => {
    track({
      type: 'tariff_code_suggestion_selected',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.TariffCodeCategory]),
        suggestion: s.Name,
        code: s.HS,
        search: props.itemDescription
      }
    });
    props.selectCode(GetTariffCodeFromSuggestion(props.tariffSystem, s));
  };

  const mainSuggestion = suggestions[0];

  if (!mainSuggestion) {
    return props.noResultsMessage && props.itemDescription ? (
      <>{props.showLoader && loading ? <P2GProgress /> : props.noResultsMessage}</>
    ) : null;
  }

  return (
    <>
      <TariffSuggestion suggestion={mainSuggestion} tariffSystem={props.tariffSystem} title={props.title}>
        <P2GLinkGroup
          links={[
            props.canSelect && (
              <P2GLink variant="body2" onClick={() => onClick(mainSuggestion)}>
                Select
              </P2GLink>
            ),
            <SelectOrCopy
              code={GetTariffCodeFromSuggestion(props.tariffSystem, mainSuggestion)}
              display="Copy"
              copied="Copied"
              variant="body2"
            />,
            !showMore && props.limit > 1 && (
              <P2GLink variant="body2" onClick={() => setShowMore(true)}>
                Show More
              </P2GLink>
            )
          ]}
        />
      </TariffSuggestion>
      {suggestions.length > 1 && showMore && (
        <P2GBox>
          {suggestions.slice(1).map((s, i) => (
            <P2GBox mt={1} key={`${s.Name}_${i}`}>
              <P2GFade in timeout={(i + 1) * 450}>
                <TariffSuggestion suggestion={s} tariffSystem={props.tariffSystem}>
                  <P2GLinkGroup
                    links={[
                      props.canSelect && (
                        <P2GLink variant="body2" onClick={() => onClick(s)}>
                          Select
                        </P2GLink>
                      ),
                      <SelectOrCopy
                        variant="body2"
                        code={GetTariffCodeFromSuggestion(props.tariffSystem, s)}
                        display="Copy"
                      />
                    ]}
                  />
                </TariffSuggestion>
              </P2GFade>
            </P2GBox>
          ))}
        </P2GBox>
      )}
    </>
  );
};

export { TariffSuggestions };
